/**
 * Meta Pixel (Facebook Pixel) event tracking helper.
 *
 * The base pixel + PageView is already wired in `/public/index.html`.
 * This file lets any form call `trackLead({...})` after a successful submit
 * to fire Meta's standard `Lead` event with useful parameters.
 *
 * Usage:
 *   import { trackLead } from "@/lib/metaPixel";
 *   ...
 *   await axios.post("/api/some-endpoint", form);
 *   trackLead({
 *     content_name: "Get Started LP",
 *     form_id: "get-started-landing",
 *     email: form.email,
 *     phone: form.phone,
 *   });
 *
 * All functions are safe to call before `fbq` loads or if a user blocks
 * the pixel — they no-op silently rather than throwing.
 */

// Standard Meta events (documented at
// developers.facebook.com/docs/meta-pixel/reference).
export const META_EVENTS = {
  LEAD: "Lead",
  COMPLETE_REGISTRATION: "CompleteRegistration",
  CONTACT: "Contact",
  SUBMIT_APPLICATION: "SubmitApplication",
  INITIATE_CHECKOUT: "InitiateCheckout",
  PURCHASE: "Purchase",
};

function _fbq() {
  if (typeof window === "undefined") return null;
  return typeof window.fbq === "function" ? window.fbq : null;
}

/**
 * Fire ANY Meta Pixel event. Prefer the specific helpers below.
 * @param {string} eventName — standard Meta event name (e.g. "Lead")
 * @param {object} params    — custom parameters (email, phone, value, currency, content_name, ...)
 */
export function trackPixelEvent(eventName, params = {}) {
  const fbq = _fbq();
  if (!fbq) return;
  try {
    // Meta hashes email/phone client-side for Advanced Matching when passed
    // through the second `init` argument, but we also include them in the
    // event `params` so they show up in Test Events / conversion match rate.
    fbq("track", eventName, params);
  } catch {
    // Pixel errors must never break the checkout / lead-capture UX.
  }
}

/**
 * Fire a `Lead` event. Call this from EVERY public form's success handler.
 * @param {object} opts
 *   - content_name (string): human-readable label, e.g. "Get Started LP"
 *   - form_id      (string): stable machine identifier, e.g. "get-started-landing"
 *   - email        (string): the lead's email (sent to Meta for matching)
 *   - phone        (string): E.164 or raw — Meta will hash it
 *   - value        (number): optional lead value (USD) for LTV modeling
 *   - currency     (string): defaults to "USD"
 */
export function trackLead({ content_name, form_id, email, phone, value, currency = "USD" } = {}) {
  const params = {
    content_name: content_name || form_id || "Lead form",
    content_category: "lead",
  };
  if (form_id) params.form_id = form_id;
  if (email) params.em = String(email).trim().toLowerCase();
  if (phone) params.ph = String(phone).replace(/[^\d+]/g, "");
  if (typeof value === "number" && value > 0) {
    params.value = value;
    params.currency = currency;
  }
  trackPixelEvent(META_EVENTS.LEAD, params);
}

/** Convenience wrappers for other high-signal events. */
export function trackContact({ email, phone, content_name } = {}) {
  const params = { content_name: content_name || "Contact" };
  if (email) params.em = String(email).trim().toLowerCase();
  if (phone) params.ph = String(phone).replace(/[^\d+]/g, "");
  trackPixelEvent(META_EVENTS.CONTACT, params);
}

export function trackCompleteRegistration({ email, phone, content_name } = {}) {
  const params = { content_name: content_name || "Registration" };
  if (email) params.em = String(email).trim().toLowerCase();
  if (phone) params.ph = String(phone).replace(/[^\d+]/g, "");
  trackPixelEvent(META_EVENTS.COMPLETE_REGISTRATION, params);
}
